import React, { useState, useEffect, useCallback } from "react";
import { useParams } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  Heading,
  Button,
  useToast,
  useColorModeValue,
  Flex,
  VStack,
  HStack,
  Badge,
  Select,
  Textarea,
  Switch,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Domain, Category } from '../../Platform/Common/types';

interface UIFaqItem {
  uiId: string;      // frontend-only
  question: string;
  answer: string;
}
  const B2CReviewDomain = () => {
    // ────────────────────────────────────────────────────────────────────────────
    // Theme & routing hooks
    // ────────────────────────────────────────────────────────────────────────────

    const cardbg = useColorModeValue("gray.50", "gray.700");
  
    const { domain_id } = useParams<{ domain_id: string }>();
    const [domainId, setDomainId] = useState<number>(Number(domain_id));
    const [faqs, setFaqs] = useState<UIFaqItem[]>([]);
    const { category_id } = useParams<{ category_id: string }>();
    const navigate = useNavigate();
    const toast = useToast();

  
    // ────────────────────────────────────────────────────────────────────────────
    // State: core domain, question-sets, mappings
    // ────────────────────────────────────────────────────────────────────────────

  
    const [domain, setDomain] = useState<Domain>({
        domain_id: 0, 
        category_id :Number(category_id), 
        name: '', 
        slug:'', 
        description: '', 
        hero_text:'', 
        hero_headline:'', 
        promo_headline:'', 
        promo_text:'', 
        promo_coupon_code: '',
        promo_active:true, 
        sequence:0,
        show_as_coming_soon:false,
        long_description:'',  
        meta_description:'', 
        meta_title:'' , 
        is_active: true, 
        created_at: new Date(), 
        updated_at: new Date()
    });


    const [categories, setCategories] = useState<Category[]>([]);
  
    const [isDomainEdited, setIsDomainEdited] = useState(false);

    const [editSessionKey, setEditSessionKey] = useState(0);
  
  
    // ────────────────────────────────────────────────────────────────────────────
    // Mode & button logic
    // ────────────────────────────────────────────────────────────────────────────
    const [isEditing, setIsEditing] = useState(false);
    const isCreating = domainId === 0;
    const isViewMode = !isEditing;

  
    const showActionButtons    = !isCreating;
    const showSaveButton       = isCreating || !isViewMode;
    const isUpdateButtonEnabled   = showActionButtons && isViewMode ;
 

  
    // ────────────────────────────────────────────────────────────────────────────
    // API fetchers
    // ────────────────────────────────────────────────────────────────────────────
    const fetchDomain = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.get<Domain>(`${b2c_apiurl}/domain/${domain_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setDomain(data);
    };

    // Write fetchDomainCategories, API has been written and then use Accordion logic - take help from ReviewCourseTerms


    const fetchCategories = async () => {
      try {
        const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
        const response = await axios.get(`${b2c_apiurl}/categories`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
          },
        });
        setCategories(response.data);
      } catch (error) {
        console.error("Failed to load B2C Categories", error);
      }
    };

    // ────────────────────────────────────────────────────────────────────────────
    // Derived state effects
    // ────────────────────────────────────────────────────────────────────────────

    const loadAllData = useCallback(async () => {
        const token = localStorage.getItem("b2c_token");
        if (!token) return;
      
        // 2) if editing an existing domain
        if (Number(domain_id) > 0) {
          await fetchDomain();

          setDomainId(Number(domain_id));
        }
        // 3) fetch all remaining lookups
          await Promise.all([
            fetchCategories(),
          ]);
     
        // 4) reset any “edited” flags
        setIsEditing(false);
        setIsDomainEdited(false);
      }, [
        domain_id,
        fetchDomain,
        fetchCategories,
 
      ]);

    // 1) On load —  fetch everything
    useEffect(() => {
      (async () => {
        const token = localStorage.getItem("b2c_token");
        if (!token) return;
  
  
        if (Number(domain_id) > 0) {
          await fetchDomain();
          setDomainId(Number(domain_id));
          }
          await fetchCategories();

      })();
    }, [domain_id]);

    useEffect(() => {
      if (domain?.faq_json) {
        setFaqs(
          domain.faq_json.map(faq => ({
            uiId: crypto.randomUUID(),
            question: faq.question,
            answer: faq.answer
          }))
        );
      } else {
        setFaqs([]);
      }
    }, [domain?.domain_id]);

  

  
    // ────────────────────────────────────────────────────────────────────────────
    // Form handlers
    // ────────────────────────────────────────────────────────────────────────────
const handleInputChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
) => {
            const { name, value, type } = e.target;

            const finalValue =
              type === "checkbox"
                ? (e.target as HTMLInputElement).checked
                : value;

  setDomain(prev => ({
    ...prev,
    [name]: finalValue,
  }));

  setIsDomainEdited(true);
};


const updateFAQ = (
  uiId: string,
  field: "question" | "answer",
  value: string
) => {
  setFaqs(prev =>
    prev.map(faq =>
      faq.uiId === uiId
        ? { ...faq, [field]: value }
        : faq
    )
  );
  setIsDomainEdited(true);
};

const addFAQ = () => {
  setFaqs(prev => [
    ...prev,
    { uiId: crypto.randomUUID(), question: "", answer: "" }
  ]);
  setIsDomainEdited(true);
};

const removeFAQ = (uiId: string) => {
  setFaqs(prev => prev.filter(faq => faq.uiId !== uiId));
  setIsDomainEdited(true);
};


  
    // ────────────────────────────────────────────────────────────────────────────
    // Save / Create / Update / Publish / Cancel / Terminate
    // ────────────────────────────────────────────────────────────────────────────
    const handleCreateDomain = async () => {
      try {
        const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';



            // Build payload properly
          const payload: Domain = {
            ...domain,
            faq_json: faqs.map(({ question, answer }) => ({
              question,
              answer
            }))
          };

        console.log("Domain payload", payload);

        const { data } = await axios.post<Domain>(`${b2c_apiurl}/create-domain/`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        });
        const newId = data.domain_id;
        // re-map question-sets

        toast({ title: "Domain created", status: "success" });

        setIsDomainEdited(false);
   
      //  navigate(`/practice/app/b2c-domain-manager/review-b2c-domain/${newId}`);

        navigate(`/practice/app/b2c-category/${category_id}/review-domain/${newId}`);
      
     } catch (err: any) {
    console.error(err);

        const message =
          err?.response?.data?.detail ||
          "Error creating domain";

        toast({
          title: message,
          status: "error",
        });
      }
    };
  
    const handleUpdateDomain = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';

        // Build payload properly
          const payload: Domain = {
            ...domain,
            faq_json: faqs.map(({ question, answer }) => ({
              question,
              answer
            }))
          };

        console.log("Domain payload", payload);

      // guard
      try {
        await axios.put(`${b2c_apiurl}/category/${category_id}/update-domain/${domain_id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        });
        toast({ title: "Domain updated", status: "success" });
        setIsDomainEdited(false);
        loadAllData();
     } catch (err: any) {
    console.error(err);

        const message =
          err?.response?.data?.detail ||
          "Error updating domain";

        toast({
          title: message,
          status: "error",
        });
      }
    };

    
  
    const handleSaveDomain = () => {

      isCreating ? handleCreateDomain() : handleUpdateDomain();
    //  setIsDomainEdited(false);
    };
  
    const handleUpdateClick = async () => {
        setIsEditing(true);
          // start a brand new edit session
        setEditSessionKey(prev => prev + 1);
    }


      const handleCancelOperation = () =>{
        setIsEditing(false);  
         loadAllData();
           // invalidate ALL child component state
        setEditSessionKey(prev => prev + 1);
        };

    

  return (
    <Box as="main" mx="auto" p={10} >

 {/* Domain Details Top Bar */}
    <Box bg={useColorModeValue("gray.100", "gray.700")} p={4} borderRadius="sm" mb={4}>
    <Flex direction={["column", "row"]} justify="space-between" align={["flex-start", "center"]} wrap="wrap">
        <Heading size="lg">{domain.name || "Untitled Domain"}</Heading>
        <HStack spacing={2} mt={[2, 0]}>
        
        </HStack>
    </Flex>

    <Flex mt={3} wrap="wrap" fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
        <Text mr={4}><strong>Created:</strong> {new Date(domain.created_at).toLocaleDateString()}</Text>
        <Text><strong>Updated:</strong> {new Date(domain.updated_at).toLocaleDateString()}</Text>
    </Flex>
    </Box>

{/* Operations Buttons */}
    <Box>
    {showActionButtons && (
        <HStack spacing={2} mt={4}>
            <Button
            size="sm"
            borderRadius="sm"
            onClick={handleUpdateClick}
            isDisabled={!isUpdateButtonEnabled}
            colorScheme="blue"
            >
            Update Domain
            </Button>


          {isEditing && (
            <Button size={"sm"} variant="outline" colorScheme="red" onClick={handleCancelOperation}>
              Cancel
            </Button>
          )}

        </HStack>
        )}
    </Box>

    {/* Domain Title & Description */}
{/* ===========================
   DOMAIN REVIEW CONTAINER
=========================== */}
<Box mt={4}>

  {/* ===========================
      CORE INFORMATION
  =========================== */}
  <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
    <fieldset disabled={!showSaveButton}>
      <Text mb={4} fontSize="md" fontWeight="bold">
        Core Information
      </Text>

      <VStack spacing={4} align="stretch">

        {/* Title */}
        <FormControl id="name" isRequired>
          <Input
            name="name"
            placeholder="Domain Title*"
            size="sm"
            value={domain.name}
            onChange={handleInputChange}
          />
        </FormControl>

        {/* Slug */}
        <FormControl id="slug" isRequired>
          <Input
            name="slug"
            placeholder="Slug*"
            size="sm"
            value={domain.slug}
            onChange={handleInputChange}
          />
        </FormControl>

        {/* Description */}
        <FormControl id="description">
          <Textarea
            name="description"
            placeholder="Domain Description"
            size="sm"
            value={domain.description || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        {/* Category */}
        <FormControl id="category_id" isRequired>
          <Flex align="center" gap={4}>
            <FormLabel minWidth="150px" mb="0" fontSize="sm">
              Category
            </FormLabel>
            <Select
              placeholder="Select Category"
              name="category_id"
              size="sm"
              value={domain.category_id}
              onChange={handleInputChange}
            >
              {categories.map((category) => (
                <option
                  key={category.category_id}
                  value={category.category_id}
                >
                  {category.name}
                </option>
              ))}
            </Select>
          </Flex>
        </FormControl>

        {/* Active Toggle */}
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="is_active" mb="0" fontSize="sm">
            Active
          </FormLabel>
          <Switch
            id="is_active"
            name="is_active"
            isChecked={domain.is_active}
            onChange={handleInputChange}
          />
        </FormControl>

        {/* Coming soon Toggle */}
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="show_as_coming_soon" mb="0" fontSize="sm">
            Show as coming soon ?
          </FormLabel>
          <Switch
            id="show_as_coming_soon"
            name="show_as_coming_soon"
            isChecked={domain.show_as_coming_soon}
            onChange={handleInputChange}
          />
        </FormControl>

      </VStack>
    </fieldset>
  </Box>

  {/* ===========================
      MARKETING SECTION
  =========================== */}
  <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
    <fieldset disabled={!showSaveButton}>
      <Text mb={4} fontSize="md" fontWeight="bold">
        Marketing & Promotion
      </Text>

      <VStack spacing={4} align="stretch">

        <FormControl id="hero_headline">
          <Input
            name="hero_headline"
            placeholder="Hero Headline"
            size="sm"
            value={domain.hero_headline || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="hero_text">
          <Textarea
            name="hero_text"
            placeholder="Hero Text"
            size="sm"
            value={domain.hero_text || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="promo_headline">
          <Input
            name="promo_headline"
            placeholder="Promo Headline"
            size="sm"
            value={domain.promo_headline || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="promo_text">
          <Textarea
            name="promo_text"
            placeholder="Promo Text"
            size="sm"
            value={domain.promo_text || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="promo_coupon_code">
          <Input
            name="promo_coupon_code"
            placeholder="Promo Coupon Code"
            size="sm"
            value={domain.promo_coupon_code || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="promo_active" mb="0" fontSize="sm">
            Promo Active
          </FormLabel>
            <Switch
            id="promo_active"
            name="promo_active"
            isChecked={domain.promo_active}
            onChange={handleInputChange}
            />
        </FormControl>

        {faqs.map((faq, index) => (
          <Box key={faq.uiId} borderWidth="1px" borderRadius="md" p={4} mb={4}>
            
            {/* Collapsible Header */}
            <Flex justify="space-between" align="center">
              <Text fontWeight="bold">
                {faq.question || `FAQ ${index + 1}`}
              </Text>

              <Button
                size="sm"
                colorScheme="red"
                onClick={() => removeFAQ(faq.uiId)}
              >
                Delete
              </Button>
            </Flex>

            <Box mt={4}>
              <FormControl mb={3}>
                <FormLabel>Question</FormLabel>
                <Input
                  value={faq.question}
                  onChange={(e) =>
                    updateFAQ(faq.uiId, "question", e.target.value)
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Answer</FormLabel>
                <Textarea
                  value={faq.answer}
                  onChange={(e) =>
                    updateFAQ(faq.uiId, "answer", e.target.value)
                  }
                />
              </FormControl>
            </Box>

          </Box>
        ))}
        <Button
          mt={2}
          colorScheme="blue"
          variant="outline"
          onClick={addFAQ}
        >
          + Add FAQ
        </Button>

        {/* Description */}
        <FormControl id="long_description">
          <Textarea
            name="long_description"
            placeholder="Domain Long Description"
            size="sm"
            value={domain.long_description || ""}
            onChange={handleInputChange}
          />
        </FormControl>

      </VStack>
    </fieldset>
  </Box>

  {/* ===========================
      SEO SECTION
  =========================== */}
  <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
    <fieldset disabled={!showSaveButton}>
      <Text mb={4} fontSize="md" fontWeight="bold">
        SEO Metadata
      </Text>

      <VStack spacing={4} align="stretch">

        <FormControl id="meta_title">
          <Input
            name="meta_title"
            placeholder="Meta Title"
            size="sm"
            value={domain.meta_title || ""}
            onChange={handleInputChange}
          />
        </FormControl>

        <FormControl id="meta_description">
          <Textarea
            name="meta_description"
            placeholder="Meta Description"
            size="sm"
            value={domain.meta_description || ""}
            onChange={handleInputChange}
          />
        </FormControl>

      </VStack>
    </fieldset>
  </Box>

  {/* ===========================
      SYSTEM INFORMATION
  =========================== */}
  <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
    <Text mb={4} fontSize="md" fontWeight="bold">
      System Information
    </Text>

    <VStack spacing={3} align="stretch">

      <Flex justify="space-between">
        <Text fontSize="sm" fontWeight="medium">Sequence</Text>
        <Text fontSize="sm">
          {domain.sequence > 0
            ? domain.sequence
            : "Not assigned yet"}
        </Text>
      </Flex>

      <Flex justify="space-between">
        <Text fontSize="sm" fontWeight="medium">Created</Text>
        <Text fontSize="sm">
          {domain.created_at
            ? new Date(domain.created_at).toLocaleString()
            : "-"}
        </Text>
      </Flex>

      <Flex justify="space-between">
        <Text fontSize="sm" fontWeight="medium">Last Updated</Text>
        <Text fontSize="sm">
          {domain.updated_at
            ? new Date(domain.updated_at).toLocaleString()
            : "-"}
        </Text>
      </Flex>

    </VStack>
  </Box>

      {/* Save Button */}
    {showSaveButton && (
      <Box textAlign="left" mt={4}>
        <Button
          mb={2}
          colorScheme="teal"
          size="sm"
          borderRadius="sm"
          onClick={handleSaveDomain}
          isDisabled={!isDomainEdited}
        >
          Commit
        </Button>
      </Box>
    )}

</Box>
   
      
    </Box>
  );
};

export default B2CReviewDomain;
